import React, { useEffect, useState } from 'react'
import {
  Autocomplete,
  Box,
  Chip,
  CircularProgress,
  Grid,
  Modal,
  styled,
  TextField,
  Typography,
  Button,
  DialogActions,
  Fade,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  useTheme,
  useMediaQuery,
} from '@mui/material'
import { BorderButtonPurple, CustomButton } from '../buttons'
import lightTheme from '../../styles/theme/lightTheme'
import '../../styles/components/_box.scss'
import '../../styles/components/_grids.scss'
import '../../styles/components/_inputs.scss'
import Api from '../../services/api.service'
import { handlingErrors } from '../../utils'
import { useDispatch } from 'react-redux'
import { userLogout } from '../../store/slices/userAuthReducer'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faClose,
  faSquarePlus,
  faTrash,
} from '@fortawesome/pro-solid-svg-icons'
import { useTranslation } from 'react-i18next'
import { niveauCompetencesList } from '../../data'
import { TextInputClassic } from '../inputs'
import { faXmark } from '@fortawesome/pro-regular-svg-icons'
import OutlineButton from '../buttons/OutlineButton'
import GradientButton from '../buttons/GradientButton'

const ModalContainer = styled(Box)(({ theme }) => ({
  width: 'calc(100% - 32px)',
  maxWidth: 800,
  minHeight: 400,
  maxHeight: '90vh',
  backgroundColor: '#FFFFFF',
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  borderRadius: '16px',
  boxShadow: '0 20px 40px rgba(0, 0, 0, 0.15)',
  padding: '32px',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'auto',

  '&:focus-visible': {
    outline: 'none',
  },

  [theme.breakpoints.up('sm')]: {
    width: 800,
    padding: '20px 40px',
  },
}))

const BoxContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  gap: '24px',
}))

const ListRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  flexWrap: 'wrap',
  alignItems: 'flex-start',
  gap: '12px !important',
  width: '100%',
  padding: '12px !important',
  borderRadius: 16,
  backgroundColor: '#f8fafc',
  border: '1px solid rgba(226, 232, 240, 0.9)',
  [theme.breakpoints.up('sm')]: {
    gap: '16px !important',
  },
}))

const FieldWrapper = styled(Box)(() => ({
  flex: '1 1 45%',
  minWidth: 0,
}))

const DeleteIconButton = styled(IconButton)(({ theme }) => ({
  width: 44,
  height: 44,
  alignSelf: 'center',
  borderRadius: 14,
  border: '1px solid rgba(148, 163, 184, 0.5)',
  color: '#475569',
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: 'rgba(226, 232, 240, 0.4)',
    borderColor: 'rgba(100, 116, 139, 0.4)',
  },
}))

const AddRowButton = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 10,
  width: '100%',
  padding: 14,
  borderRadius: 16,
  border: '1px dashed rgba(148, 163, 184, 0.9)',
  backgroundColor: '#ffffff',
  color: '#334155',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: '#f8fafc',
    borderColor: 'rgba(99, 102, 241, 0.4)',
  },
  [theme.breakpoints.up('sm')]: {
    p: '16px 18px',
  },
}))

const EditList = ({
  open,
  onClose,
  onSave,
  values,
  datas,
  id,
  role = 'candidat',
  type,
}) => {
  const newDatas = [...new Set(datas)]
  const { t, i18n } = useTranslation()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)
  const [dataValue, setDataValue] = useState(values)
  const [title, setTitle] = useState('')
  const dispatch = useDispatch()

  useEffect(() => {
    switch (type) {
      case 'competences':
        setTitle(t('filter.competences') + ' (max 6)')
        break
      case 'competencesAnnexes':
        setTitle(t('filter.competencesAnnexes'))
        break
      case 'valeurs':
        setTitle(t('filter.values'))
        break
      case 'softskils':
        setTitle(t('filter.softskils'))
        break
      default:
        break
    }
    if (
      type !== 'competences' &&
      type !== 'competencesAnnexes' &&
      dataValue.length < 6
    ) {
      setError(true)
    } else {
      setError(false)
    }
  }, [type])

  const onSubmit = async () => {
    setError(false)
    if (
      type !== 'competences' &&
      type !== 'competencesAnnexes' &&
      dataValue.length < 6
    ) {
      setError(true)
      return
    }
    setLoading(true)
    try {
      const result = await Api.candidate.edit(id, {
        [type]:
          type === 'competences' || type === 'competencesAnnexes'
            ? competences
            : dataValue,
      })
      const handling = handlingErrors(result)

      if (handling.status === 401) {
        dispatch(userLogout())
        return
      }

      if (handling.ok) {
        //const res = handling.data
        setLoading(false)
        onSave()
      } else {
        console.error('erreur')
        setLoading(false)
      }
    } catch (error) {
      console.error('erreur submit !')
    }
  }

  const onSubmitCompany = async () => {
    setLoading(true)
    try {
      const result = await Api.company.edit(id, { [type]: dataValue })
      const handling = handlingErrors(result)

      if (handling.status === 401) {
        dispatch(userLogout())
        return
      }

      if (handling.ok) {
        //const res = handling.data
        setLoading(false)
        onSave()
      } else {
        console.error('erreur')
        setLoading(false)
      }
    } catch (error) {
      console.error('erreur submit !')
    }
  }

  const [competences, setCompetences] = useState(
    type === 'competences' || type === 'competencesAnnexes' ? values : []
  )
  const [refresh, setRefresh] = useState(1)
  const handleAddCompetence = () => {
    competences.push({ competence: '', niveau: niveauCompetencesList[0].id })
    setCompetences(competences)
    setRefresh(Math.random())
  }

  const theme = useTheme()
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'))

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={fullScreen}
      scroll={'paper'}
      PaperProps={{
        sx: {
          borderRadius: fullScreen ? 0 : '16px',
          p: fullScreen ? 0 : '8px',
          m: fullScreen ? 0 : undefined,
          maxHeight: fullScreen ? '100%' : '90vh',
          ...(fullScreen && {
            display: 'flex',
            flexDirection: 'column',
          }),
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 1,
          px: { xs: 2, sm: 3 },
          py: { xs: 1.5, sm: 2 },
          pb: 2,
          flexShrink: 0,
          borderBottom: '1px solid rgba(229, 231, 235, 0.8)',
          backgroundColor: 'rgba(249, 250, 251, 0.5)',
        }}
      >
        <Typography
          variant={fullScreen ? 'subtitle1' : 'h6'}
          sx={{
            fontWeight: 600,
            color: '#1f2937',
            pr: 1,
            lineHeight: 1.3,
            wordBreak: 'break-word',
          }}
        >
          {t('box.title.editList')} {title}
        </Typography>
        <IconButton
          onClick={onClose}
          sx={{
            color: '#6b7280',
            flexShrink: 0,
            '&:hover': {
              backgroundColor: 'rgba(107, 114, 128, 0.1)',
              color: '#374151',
            },
          }}
        >
          <FontAwesomeIcon icon={faXmark} />
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={{
          px: { xs: 2, sm: 4 },
          py: { xs: 2, sm: 3 },
          pt: { xs: '16px !important', sm: '32px !important' },
          flex: fullScreen ? 1 : undefined,
          overflow: 'auto',
        }}
      >
        {type === 'competences' || type === 'competencesAnnexes' ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              maxHeight: { xs: 'none', sm: 450 },
              overflowY: { xs: 'visible', sm: 'auto' },
              pr: { xs: 0, sm: 1 },
            }}
          >
            {competences.map((cmpt, index) => (
              <ListRow key={`competence-${index}`}>
                <FieldWrapper sx={{ flex: {xs: '1 1 100%', sm: '1 1 45%'} }}>
                  <TextInputClassic
                    type={'text'}
                    width={'100%'}
                    onChange={(event) => {
                      const newCompetences = [...competences]
                      newCompetences[index].competence = event.target.value
                      setCompetences(newCompetences)
                      setRefresh(Math.random())
                    }}
                    value={competences[index].competence}
                  />
                </FieldWrapper>
                <FieldWrapper>
                  <Autocomplete
                    disableClearable
                    value={niveauCompetencesList[cmpt.niveau - 1]}
                    sx={{ width: '100%', minWidth: 0, height: 45 }}
                    onChange={(event, newValue) => {
                      const newCompetences = [...competences]
                      newCompetences[index].niveau = newValue.id
                      setCompetences(newCompetences)
                      setRefresh(Math.random())
                    }}
                    isOptionEqualToValue={(option, value) =>
                      option.titre === value.titre
                    }
                    getOptionLabel={(option) => option.titre[i18n.language]}
                    options={niveauCompetencesList}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        sx={{ backgroundColor: '#fff' }}
                        InputProps={{
                          ...params.InputProps,
                        }}
                      />
                    )}
                  />
                </FieldWrapper>
                <DeleteIconButton
                  onClick={(e) => {
                    e.preventDefault()
                    const newCompetences = [...competences]
                    newCompetences.splice(index, 1)
                    setCompetences(newCompetences)
                    setRefresh(Math.random())
                  }}
                  aria-label={t('button.delete')}
                >
                  <FontAwesomeIcon icon={faTrash} fontSize={16} />
                </DeleteIconButton>
              </ListRow>
            ))}

            {competences.length < 6 && (
              <AddRowButton onClick={handleAddCompetence}>
                <FontAwesomeIcon
                  icon={faSquarePlus}
                  fontSize={18}
                  color={'#4b5563'}
                />
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {t('box.addOtherCompetence')}
                </Typography>
              </AddRowButton>
            )}
          </Box>
        ) : (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
            }}
          >
            <Autocomplete
              sx={{
                width: '100%',
                backgroundColor: '#fff',
                '& .MuiAutocomplete-tag': {
                  maxWidth: '100%',
                  whiteSpace: 'normal',
                  wordBreak: 'break-word',
                },
                '& .MuiAutocomplete-tagView': {
                  padding: '6px 10px',
                },
              }}
              multiple
              freeSolo={
                type === 'competences' || type === 'competencesAnnexes'
                  ? true
                  : false
              }
              defaultValue={dataValue}
              options={newDatas}
              onChange={(event, newValue) => {
                if (type === 'competences') {
                  setDataValue(newValue.slice(0, 6))
                } else {
                  if (type !== 'competencesAnnexes' && newValue.length < 6) {
                    setError(true)
                  } else {
                    setError(false)
                  }
                  setDataValue(newValue)
                }
              }}
              renderTags={(selectedValues, getTagProps) => (
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 1,
                    py: 0.5,
                  }}
                >
                  {(type === 'competences'
                    ? selectedValues.slice(0, 6)
                    : selectedValues
                  ).map((option, index) => (
                    <Chip
                      key={`${option}-${index}`}
                      variant="outlined"
                      label={option}
                      sx={{
                        maxWidth: '100%',
                        whiteSpace: 'normal',
                      }}
                      {...getTagProps({ index })}
                    />
                  ))}
                </Box>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={
                    type === 'competences' || type === 'competencesAnnexes'
                      ? t('filter.competencesTextCandidat')
                      : t('box.addthe') + title
                  }
                  error={error}
                  helperText={
                    error
                      ? type === 'competencesAnnexes'
                        ? t('errorMessage.listCompAnn')
                        : type === 'softskils'
                        ? t('errorMessage.listCompSoftskills')
                        : t('errorMessage.list')
                      : ''
                  }
                />
              )}
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions
        sx={{
          px: { xs: 2, sm: 4 },
          py: { xs: 2, sm: 3 },
          gap: 2,
          justifyContent: 'center',
          backgroundColor: 'rgba(249, 250, 251, 0.8)',
          borderTop: '1px solid rgba(229, 231, 235, 0.8)',
          flexDirection: { xs: 'column', sm: 'row' },
          flexShrink: 0,
          '& > *': {
            width: { xs: '100%', sm: 'auto' },
            m: 0,
          },
        }}
      >
        <OutlineButton onClick={onClose} fullWidth={fullScreen}>
          {t('button.cancel')}
        </OutlineButton>

        <GradientButton
          onClick={role === 'entreprise' ? onSubmitCompany : onSubmit}
          fullWidth={fullScreen}
        >
          {loading ? (
            <>
              <CircularProgress size={16} sx={{ mr: 1, color: 'white' }} />
              {t('profile.saving')}
            </>
          ) : (
            t('button.save')
          )}
        </GradientButton>
      </DialogActions>
    </Dialog>
  )
}

export default EditList
